import { pdf } from '@react-pdf/renderer';
import { format } from 'date-fns';
import type { TimeEntry } from '../../../core/models/TimeEntry';
import { TimeEntryPdfDocument } from './TimeEntryPdfDocument';
import type { TimeEntryExportContext } from './timeEntryExportTypes';

export type { TimeEntryExportContext } from './timeEntryExportTypes';

const formatDuration = (minutes: number) => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours}:${mins.toString().padStart(2, '0')}`;
};

const formatHours = (minutes: number) => (minutes / 60).toFixed(2);

const safeFilePart = (value: string) =>
  value
    .trim()
    .replace(/[^A-Za-z0-9._-]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .toLowerCase() || 'time-records';

const downloadBlob = (blob: Blob, fileName: string) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const csvEscape = (value: unknown) => {
  const text = value == null ? '' : String(value);
  return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

export async function exportTimeEntriesPdf(entries: TimeEntry[], context: TimeEntryExportContext) {
  const blob = await pdf(<TimeEntryPdfDocument entries={entries} context={context} />).toBlob();
  downloadBlob(blob, `${safeFilePart(context.projectName || context.clientName || 'time-records')}-time-records.pdf`);
}

export function exportTimeEntriesCsv(entries: TimeEntry[], context: TimeEntryExportContext) {
  const rows = [
    ['Date', 'Start', 'End', 'Client', 'Project', 'User', 'Description', 'Type', 'Duration', 'Hours', 'Billable', 'Status'],
    ...entries.map((entry) => [
      format(entry.startTime, 'yyyy-MM-dd'),
      format(entry.startTime, 'HH:mm'),
      format(entry.endTime, 'HH:mm'),
      context.clientName || entry.clientId || entry.organizationId,
      entry.projectId
        ? context.projectNamesById?.[entry.projectId] || entry.projectId
        : 'Client only',
      context.userNamesById?.[entry.userId] || entry.userId,
      entry.description,
      entry.type || '',
      formatDuration(entry.duration),
      formatHours(entry.duration),
      entry.billable ? 'Yes' : 'No',
      entry.status,
    ]),
  ];

  const csv = rows.map((row) => row.map(csvEscape).join(',')).join('\n');
  downloadBlob(
    new Blob([csv], { type: 'text/csv;charset=utf-8' }),
    `${safeFilePart(context.projectName || context.clientName || 'time-records')}-time-records.csv`
  );
}
